import { type Attribution, getAttribution } from './attribution';

/**
 * Campaign attribution for the multi-channel acquisition funnel.
 *
 * `getAttribution()` reads the CURRENT url, which is only correct for an event
 * that fires on the landing page itself. A visitor who arrives on
 * `/blog/x?utm_source=tiktok`, reads the post, then signs up from `/waitlist`
 * presents no utm params by the time the conversion happens — so the channel that
 * actually earned the lead is lost exactly when it matters.
 *
 * This module persists the FIRST touch and derives a normalized channel, so both
 * the acquiring channel and the most recent one ride along on every event as
 * Mixpanel super properties.
 */

/**
 * The fixed channel vocabulary. `utm_source` is free text typed into a link by
 * whoever built the campaign: without folding it to a closed set, `tiktok`,
 * `TikTok` and `tik-tok` become three rows and every per-channel number splits
 * across them.
 */
export const CAMPAIGN_CHANNELS = [
  'instagram',
  'linkedin',
  'tiktok',
  'google',
  'blog',
  'email',
  'direct',
  'referral',
  'other',
] as const;

export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

export const FIRST_TOUCH_STORAGE_KEY = 'flowsell:attribution:first_touch';

/** Canonical form of a utm_source: lowercased, separators stripped. */
const fold = (value: string): string => value.toLowerCase().replace(/[\s._-]/g, '');

const SOURCE_TO_CHANNEL: ReadonlyMap<string, CampaignChannel> = new Map([
  ['instagram', 'instagram'],
  ['ig', 'instagram'],
  ['linkedin', 'linkedin'],
  ['li', 'linkedin'],
  ['tiktok', 'tiktok'],
  // Paid search arrives under whichever of these the ad platform or the person
  // building the link happened to use.
  ['google', 'google'],
  ['googleads', 'google'],
  ['adwords', 'google'],
  ['blog', 'blog'],
  ['email', 'email'],
  ['newsletter', 'email'],
  ['direct', 'direct'],
]);

// Instagram and TikTok route outbound taps through their own shortener hosts, so
// the referrer is not always the obvious domain.
const REFERRER_HOST_TO_CHANNEL: ReadonlyArray<readonly [RegExp, CampaignChannel]> = [
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)linkedin\.com$/, 'linkedin'],
  [/(^|\.)lnkd\.in$/, 'linkedin'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
];

function referrerHost(referrer: string | undefined): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname;
  } catch {
    return undefined;
  }
}

export function normalizeChannel(attribution: Attribution): CampaignChannel {
  if (attribution.utmSource) {
    return SOURCE_TO_CHANNEL.get(fold(attribution.utmSource)) ?? 'other';
  }

  const host = referrerHost(attribution.referrer);
  if (host) {
    for (const [pattern, channel] of REFERRER_HOST_TO_CHANNEL) {
      if (pattern.test(host)) return channel;
    }
    return 'referral';
  }

  return 'direct';
}

// Storage is unavailable on native, and can throw on web in private modes or with
// cookies blocked. Attribution is never worth breaking a page load over.
function readStoredFirstTouch(): Attribution | undefined {
  try {
    const raw = window.localStorage?.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Attribution) : undefined;
  } catch {
    return undefined;
  }
}

function writeFirstTouch(attribution: Attribution): void {
  try {
    window.localStorage?.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Non-fatal: this visitor is attributed by last touch only.
  }
}

export interface ResolvedCampaignAttribution {
  firstTouch: Attribution;
  lastTouch: Attribution;
  firstTouchChannel: CampaignChannel;
  lastTouchChannel: CampaignChannel;
}

/**
 * Reads the current url and, on the very first visit, persists it as the first
 * touch. A direct first visit is stored too — otherwise a visitor who arrives
 * directly and returns later via an ad would be credited to the ad.
 */
export function resolveCampaignAttribution(): ResolvedCampaignAttribution {
  const lastTouch = getAttribution();
  const stored = readStoredFirstTouch();

  if (!stored) {
    writeFirstTouch(lastTouch);
  }

  const firstTouch = stored ?? lastTouch;

  return {
    firstTouch,
    lastTouch,
    firstTouchChannel: normalizeChannel(firstTouch),
    lastTouchChannel: normalizeChannel(lastTouch),
  };
}

// A type alias, not an interface: only aliases get TypeScript's implicit index
// signature, which is what makes this assignable to the Record<string, unknown>
// that MixpanelClient.register accepts.
export type CampaignSuperProperties = {
  channel: CampaignChannel;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer_domain: string | null;
  landing_path: string | null;
  first_channel: CampaignChannel;
  first_utm_source: string | null;
  first_utm_medium: string | null;
  first_utm_campaign: string | null;
  first_utm_term: string | null;
  first_utm_content: string | null;
  first_referrer_domain: string | null;
  first_landing_path: string | null;
};

/** Path without the query string — invite codes and tokens live in the query. */
const pathOnly = (landingPage: string | undefined): string | undefined =>
  landingPage ? landingPage.split('?')[0] : undefined;

/**
 * Every key is emitted on every call, `null` when the visit has no value for it.
 *
 * These are SUPER properties: Mixpanel persists them and replays them on every
 * later event. Omitting an absent key does not clear it — it leaves the previous
 * visit's value in place, which is how one event came to report
 * `channel: direct` next to `utm_source: tiktok`.
 */
const orNull = (value: string | undefined): string | null =>
  value !== undefined && value !== '' ? value : null;

/**
 * Mixpanel super properties carrying both touches. Registering these means every
 * subsequent client event — landing views, blog reads, waitlist submits, signups —
 * is attributable without each call site remembering to pass utm params.
 *
 * PII-safe by construction: only the referrer DOMAIN and the landing PATH, never
 * the raw referrer or the landing query string.
 */
export function buildCampaignSuperProperties(): CampaignSuperProperties {
  const { firstTouch, lastTouch, firstTouchChannel, lastTouchChannel } =
    resolveCampaignAttribution();

  const props: CampaignSuperProperties = {
    channel: lastTouchChannel,
    utm_source: orNull(lastTouch.utmSource),
    utm_medium: orNull(lastTouch.utmMedium),
    utm_campaign: orNull(lastTouch.utmCampaign),
    utm_term: orNull(lastTouch.utmTerm),
    utm_content: orNull(lastTouch.utmContent),
    referrer_domain: orNull(referrerHost(lastTouch.referrer)),
    landing_path: orNull(pathOnly(lastTouch.landingPage)),

    // First touch is immutable: it must survive every later campaign-less visit.
    first_channel: firstTouchChannel,
    first_utm_source: orNull(firstTouch.utmSource),
    first_utm_medium: orNull(firstTouch.utmMedium),
    first_utm_campaign: orNull(firstTouch.utmCampaign),
    // The creative and the keyword the visitor was acquired through. Without
    // these the first touch can name the campaign but not the piece inside it.
    first_utm_term: orNull(firstTouch.utmTerm),
    first_utm_content: orNull(firstTouch.utmContent),
    first_referrer_domain: orNull(referrerHost(firstTouch.referrer)),
    first_landing_path: orNull(pathOnly(firstTouch.landingPage)),
  };

  return props;
}
